import { ActionButton, Stack, TextField } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { FieldErrors } from "react-hook-form";

import { useDelayedState } from "../../../hooks/use-delayed-state/useDelayedState";
import { getDeepValue } from "../../../utils";
import { matchQuery } from "../../../utils/query/matchQuery";
import { getSortedCollection } from "../../../utils/sort/getSortedCollection";
import { TooltipHost } from "../../surfaces/tooltip/TooltipHost";
import { OutsideClickWatch } from "../../watches/OutsideClickWatch";
import { TextFieldClearButton } from "../button/TextFieldClearButton";
import { DropdownOptions } from "./common/DropdownOptions";
import { DropdownOption } from "./DropdownOption";
import { useSearchDropdownStyles } from "./useSearchDropdownStyles";

export interface SearchDropdownProps {
    selectedKey: string | undefined;
    options: DropdownOption[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    tooltip?: string;
    underlined?: boolean;
    disableSort?: boolean;
    disableClear?: boolean;
    errors?: FieldErrors;
    name?: string;
    onChange(item?: DropdownOption): void;
    testId?: string;
}

export const SearchDropdown: React.FunctionComponent<SearchDropdownProps> = ({
    selectedKey,
    options,
    label,
    placeholder,
    disabled,
    tooltip,
    underlined,
    disableSort,
    onChange,
    testId,
    errors,
    name,
    disableClear
}: SearchDropdownProps) => {
    const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>();
    const [selectedOption, setSelectedOption] = useState<DropdownOption>();

    const [isListOpen, setIsListOpen] = useState(false);

    const [delayedInput, input, setInput] = useDelayedState<string>("");

    const { listWrapperStyles, itemStyles, actionButtonStyles, closedListStyles } = useSearchDropdownStyles(
        dropdownOptions?.length ?? options.length,
        5
    );

    const optionsPanelStyles = isListOpen ? listWrapperStyles : closedListStyles;

    useEffect(() => {
        const sortedOptions = getSortedOptions(options, disableSort);
        setDropdownOptions(sortedOptions);
    }, [options, options.length]);

    useEffect(() => {
        const filteredOptions = options.filter((option: DropdownOption) => matchQuery(option.text, delayedInput));

        const sortedOptions = getSortedOptions(filteredOptions, disableSort);
        setDropdownOptions(sortedOptions);
    }, [delayedInput]);

    useEffect(() => {
        const selection = options.find((item: DropdownOption) => item.key === selectedKey);

        setSelectedOption(selection);
    }, [selectedKey, options, options.length]);

    const changeInput = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        setInput(value || "");
    };

    const clearInput = () => {
        setInput("");
    };

    const clearSelection = () => {
        onChange(undefined);
        clearInput();
    };

    const openList = () => {
        setIsListOpen(true);
    };

    const closeList = () => {
        setIsListOpen(false);
        clearInput();
    };

    const renderTextFieldButtons = () => (
        <>
            {selectedOption && !disableClear && (
                <TextFieldClearButton
                    disabled={disabled}
                    onClick={() => {
                        clearSelection();
                        closeList();
                    }}
                    testId={`${name}-clear`}
                />
            )}
            {isListOpen ? (
                <ActionButton
                    disabled={disabled}
                    iconProps={{
                        iconName: "ChevronUp"
                    }}
                    styles={actionButtonStyles}
                    data-is-focusable={false}
                    onClick={closeList}
                />
            ) : (
                <ActionButton
                    disabled={disabled}
                    iconProps={{
                        iconName: "ChevronDown"
                    }}
                    styles={actionButtonStyles}
                    onClick={openList}
                    data-is-focusable={false}
                />
            )}
        </>
    );

    return (
        <OutsideClickWatch onOutsideClick={closeList} isWatching={isListOpen}>
            <TooltipHost content={tooltip}>
                <Stack
                    styles={{ root: { display: "block", position: "relative" } }}
                    data-testtype={"dropdown"}
                    data-testid={testId}
                >
                    <TextField
                        label={label}
                        value={isListOpen ? input : selectedOption?.text}
                        placeholder={selectedOption?.text || placeholder}
                        onClick={openList}
                        onChange={changeInput}
                        onRenderSuffix={renderTextFieldButtons}
                        autoComplete="off"
                        disabled={disabled}
                        underlined={!!underlined}
                        styles={{
                            suffix: {
                                backgroundColor: "transparent",
                                padding: 0
                            },
                            root: { width: "100%" },
                            fieldGroup: getFieldGroupStyle(errors, name)
                        }}
                    />
                    <DropdownOptions
                        options={dropdownOptions ?? options}
                        optionsPanelStyles={optionsPanelStyles}
                        itemStyles={itemStyles}
                        onItemSelected={(option) => {
                            onChange(option);
                            closeList();
                        }}
                    />
                </Stack>
            </TooltipHost>
        </OutsideClickWatch>
    );
};

const getFieldGroupStyle = (errors?: FieldErrors, name?: string) => {
    if (!errors || !name) return undefined;

    return getDeepValue(errors, name) ? { border: "2px solid red" } : undefined;
};

const getSortedOptions = (options: DropdownOption[], disableSort = false) => {
    if (disableSort) {
        return options;
    }

    const optionsWithLeadingZeros = addLeadingZeros(options);

    const sortedOptions = getSortedCollection(optionsWithLeadingZeros, "text", "asc");

    return removeLeadingZeros(sortedOptions);
};

interface LeadingZerosDropdownOption extends DropdownOption {
    leadingZerosCount: number;
}

const addLeadingZeros = (options: DropdownOption[]): LeadingZerosDropdownOption[] => {
    let maxDigitsCount = 0;

    return options
        .map<DropdownOption & { digitsCount: number }>((option) => {
            const digitsCount = countLeadingDigits(option.text);

            maxDigitsCount = digitsCount > maxDigitsCount ? digitsCount : maxDigitsCount;

            return { ...option, digitsCount };
        })
        .map<LeadingZerosDropdownOption>((option) => {
            const leadingZerosCount = maxDigitsCount - option.digitsCount;

            return {
                key: option.key,
                text: `${"0".repeat(leadingZerosCount)}${option.text}`,
                leadingZerosCount
            };
        });
};

const countLeadingDigits = (text: string): number => getLeadingDigits(text).length;
const getLeadingDigits = (text: string): string => text.match(/^\d+ /g)?.[0].trim() ?? "";

const removeLeadingZeros = (options: LeadingZerosDropdownOption[]): DropdownOption[] =>
    options.map<DropdownOption>((option) => ({
        key: option.key,
        text: option.text.slice(option.leadingZerosCount)
    }));
